/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
"use client"

import type { FocusEvent, PropsWithChildren, ReactNode } from "react"
import { useRef, useSyncExternalStore } from "react"

import type * as TooltipPrimitive from "@radix-ui/react-tooltip"
import {
	InternalTooltip,
	InternalTooltipContent,
	InternalTooltipTrigger,
} from "./internal/tooltip"

interface Props {
	label: ReactNode
	shouldWrapChildren?: boolean
	side?: TooltipPrimitive.TooltipContentProps["side"]
	enabled?: boolean
	contentClassName?: string
}

const MOBILE_WIDTH_THRESHOLD = 768

const mobileQuery =
	typeof window !== "undefined"
		? window.matchMedia(`(max-width: ${MOBILE_WIDTH_THRESHOLD - 1}px)`)
		: null

function subscribeToMobileQuery(callback: () => void) {
	mobileQuery?.addEventListener("change", callback)
	return () => mobileQuery?.removeEventListener("change", callback)
}

function getIsMobile() {
	return mobileQuery?.matches ?? false
}

function getServerIsMobile() {
	return false
}

/**
 * The browser refocuses the last focused element when the window or tab
 * regains focus, and Radix opens tooltips on focus. Skip that one refocus.
 */
function useSkipWindowRefocusOpen() {
	const skipNextFocusRef = useRef(false)

	return {
		onBlur: (event: FocusEvent<HTMLElement>) => {
			// A window blur keeps the trigger as activeElement; a real focus move does not.
			skipNextFocusRef.current = document.activeElement === event.currentTarget
		},
		onFocus: (event: FocusEvent<HTMLElement>) => {
			if (!skipNextFocusRef.current) return
			skipNextFocusRef.current = false
			event.preventDefault()
		},
	}
}

export const Tooltip = ({
	label,
	children,
	shouldWrapChildren,
	side,
	contentClassName = "",
	enabled = true,
}: PropsWithChildren<Props>) => {
	const isMobile = useSyncExternalStore(
		subscribeToMobileQuery,
		getIsMobile,
		getServerIsMobile,
	)
	const refocusHandlers = useSkipWindowRefocusOpen()

	// Force disable tooltip on mobile
	if (!enabled || isMobile) {
		return <>{children}</>
	}

	return (
		<InternalTooltip>
			<InternalTooltipTrigger asChild {...refocusHandlers}>
				{shouldWrapChildren ? (
					<span tabIndex={0} className="flex">
						{children}
					</span>
				) : (
					children
				)}
			</InternalTooltipTrigger>
			<InternalTooltipContent
				side={side}
				align="center"
				onClick={(e) => {
					e.preventDefault()
				}}
				className={contentClassName}
			>
				{label}
			</InternalTooltipContent>
		</InternalTooltip>
	)
}
